import { PagingRequest } from '../common/paging';
import { TenderType } from './tender.enums';
import { Configuration } from '../../configuration';
import { Address } from '../common/address';
import { HvtSection1Dto, HvtSection2Dto, HvtSection3Dto, HvtSection4Dto, HvtSection6Dto } from '../notices/ojeu.notice.models';
import { FormType } from '../notices/ojeu.notice.enums';
import { NoticeStatus } from '../notices/notice.enums';
import { DateRangeDto } from '../common/date.range';
import { TenderTeamMemberSummaryDto } from '../tender.team.members/tender.team.member.models';

export class TenderSearchRequest extends PagingRequest {
    types: TenderType[];
    categoryIds: number[];
    locationIds: number[];
    status: NoticeStatus;
    publishedDateFrom: string;
    publishedDateTo: string;
    expiredDateFrom: string;
    expiredDateTo: string;
    registerInterestDeadlineFrom: string;
    registerInterestDeadlineTo: string;
    submitDocumentsDeadlineFrom: string;
    submitDocumentsDeadlineTo: string;
    contractStartDateFrom: string;
    contractStartDateTo: string;
    contractEndDateFrom: string;
    contractEndDateTo: string;
    id: number;
    noticeId: number;
    buyerOrganisation: string;
    buyerName: string;
    buyerEmail: string;
    ojeuId: string;
    keywords: string[];
    excludeKeywords: string[];
    supplierId: number;
    excludeBookmarked: boolean;
    excludeViewed: boolean;
    excludeAssigned: boolean;

    constructor(configuration: Configuration) {
        super(configuration);
        this.types = [TenderType.Low, TenderType.High, TenderType.PinHigh, TenderType.PinLow, TenderType.PrivateProject];
        this.status = NoticeStatus.Live;
        this.categoryIds = [];
        this.locationIds = [];
        this.keywords = [];
        this.excludeKeywords = [];
        this.excludeAssigned = false;
        this.excludeBookmarked = false;
        this.excludeViewed = false;
    }

    setValues(storage: TenderSearchStorage) {
        //this.types = storage.types;
        this.categoryIds = storage.categoryIds;
        this.locationIds = storage.locationIds;
        this.status = storage.status;
        this.id = storage.id;
        this.buyerOrganisation = storage.buyerOrganisation;
        this.buyerName = storage.buyerName;
        this.buyerEmail = storage.buyerEmail;
        this.ojeuId = storage.ojeuId;
        this.keywords = storage.keywords;
        this.excludeKeywords = storage.excludeKeywords;
        this.excludeViewed = storage.excludeViewed;
        this.excludeBookmarked = storage.excludeBookmarked;
        this.excludeAssigned = storage.excludeAssigned;
    }

    reset() {
        this.types = [TenderType.High, TenderType.Low, TenderType.PinHigh, TenderType.PinLow, TenderType.PrivateProject];
        this.categoryIds = [];
        this.locationIds = [];
        this.status = NoticeStatus.Live;
        this.publishedDateFrom = null;
        this.publishedDateTo = null;
        this.expiredDateFrom = null;
        this.expiredDateTo = null;
        this.registerInterestDeadlineFrom = null;
        this.registerInterestDeadlineTo = null;
        this.submitDocumentsDeadlineFrom = null;
        this.submitDocumentsDeadlineTo = null;
        this.contractStartDateFrom = null;
        this.contractStartDateTo = null;
        this.contractEndDateFrom = null;
        this.contractEndDateTo = null;
        this.id = null;
        this.buyerOrganisation = null;
        this.buyerName = null;
        this.buyerEmail = null;
        this.ojeuId = null;
        this.keywords = [];
        this.excludeKeywords = [];
        this.excludeAssigned = false;
        this.excludeBookmarked = false;
        this.excludeViewed = false;
    }
}

export class TenderSearchStorage {
    types: TenderType[];
    categoryIds: number[];
    locationIds: number[];
    status: NoticeStatus;
    id: number;
    noticeId: number
    buyerOrganisation: string;
    buyerName: string;
    buyerEmail: string;
    ojeuId: string;
    keywords: string[];
    excludeKeywords: string[];
    publishedDate: DateRangeDto;
    expiredDate: DateRangeDto;
    registerInterestDeadline: DateRangeDto;
    submitDocumentsDeadline: DateRangeDto;
    contractStartDate: DateRangeDto;
    contractEndDate: DateRangeDto;
    excludeBookmarked: boolean;
    excludeViewed: boolean;
    excludeAssigned: boolean;

    constructor(request: TenderSearchRequest) {
        this.types = request.types;
        this.categoryIds = request.categoryIds;
        this.locationIds = request.locationIds;
        this.status = request.status;
        this.id = request.id;
        this.noticeId = request.noticeId;
        this.buyerOrganisation = request.buyerOrganisation;
        this.buyerName = request.buyerName;
        this.buyerEmail = request.buyerEmail;
        this.ojeuId = request.ojeuId;
        this.keywords = request.keywords;
        this.excludeKeywords = request.excludeKeywords;
        this.excludeViewed = request.excludeViewed;
        this.excludeBookmarked = request.excludeBookmarked;
        this.excludeAssigned = request.excludeAssigned;
    }
}

export class TenderSummaryDto {
    id: number;
    title: string;
    b2BRef: number;
    type: string;
    location: string;
    jsInterestDeadlineDate: string;
    jsInterestDeadlineTime: string;
    remainingDaysToRegisterInterest: number;
    jsDocumentSubmissionDate: string;
    jsDocumentSubmissionTime: string;
    jsEstimatedPublication: string;
    remainingDaysToSubmitDocument: number;
    description: string;
    aiVagueDescription: string;
    buyersDescription: string;
    jsLastViewed: string;
    sentToBidManagement: boolean;
    bookmarked: boolean;
    assigned: boolean;
    bookmarkedBy: string;
    bookmarkedById: number;
    jsBookmarkedDate: string;
    jsPublishedDate: string;
    assignedToId: number;
    assignedTo: string;
    value: string;
    selected: boolean;
}

export class BookmarkedTenderSearchRequest extends PagingRequest {
    status: NoticeStatus = NoticeStatus.Live;
    teamMember: number = 0;
    types: Array<TenderType> = [TenderType.High, TenderType.Low, TenderType.PrivateB2B, TenderType.PrivateProject, TenderType.PinHigh, TenderType.PinLow];
    deadlineDateFrom: string;
    deadlineDateTo: string;

    equals(other: BookmarkedTenderSearchRequest): boolean {
        return this.status === other.status &&
            this.deadlineDateFrom === other.deadlineDateFrom &&
            this.deadlineDateTo === other.deadlineDateTo &&
            this.teamMember === other.teamMember &&
            this.types.length === other.types.length &&
            this.types.every(t => { return other.types.indexOf(t) > -1 }) &&
            super.equals(other);
    }
}

export class BookmarkedTenderSummaryDto extends TenderSummaryDto {
    bookmarkId: number;
}

export class TenderDetailsDto {
    id: number;
    officialTitle: string;
    b2BTitle: string;
    value: string;
    officialContractNumber: string;
    daysToDeadline: number;
    type: string;
    jsInterestDeadline: string;
    jsDocumentDeadline: string;
    jsClarificationDeadline: string;
    jsEstimatedPublication: string;
    jsContractStartDate: string;
    jsContractEndDate: string;
    jsZipLastViewed: string;
    buyerOrganisationName: string;
    contactName: string;
    contactEmail: string;
    contactNumber: string;
    contactAddress: Address;
    url: string;
    secureUrl: string;
    description: string;
    buyersDescription: string;
    placeholder: string;
    ojeuFormType: FormType;
    ojeuSections: Array<number>;
    isSchemaEight: boolean = true;
    notes: Array<TenderNote>;
    reminders: Array<TenderReminder>;
    noticeNote: string;
    documents: Array<NoticeDocument>;
    uploadedFromAdmin: boolean;

    hvtSection1: HvtSection1Dto;
    hvtSection2: HvtSection2Dto;
    hvtSection3: HvtSection3Dto;
    hvtSection4: HvtSection4Dto;
    hvtSection6: HvtSection6Dto;

    schema8Section1: string;
    schema8Section2: string;
    schema8Section3: string;
    schema8Section4: string;
    schema8Section5: string;
    schema8Section6: string;
    schema8Section7: string;
    schema8Section8: string;
    schema8Section9: string;

    bookmarked: boolean;
    assigned: boolean;
    bookmarkedBy: string;
    bookmarkedById: number;
    jsBookmarkedDate: string;
    assignedToId: number;
    assignedTo: string;
    jsLastViewed: string;
    sentToBidManagement: boolean;
    status: NoticeStatus;

    constructor(notSubscribed?: boolean) {
        if (notSubscribed) {
            this.placeholder = 'xxxxxxxxxx';

            this.type = this.placeholder;
            this.b2BTitle = this.placeholder;
            this.officialContractNumber = this.placeholder;
            this.jsClarificationDeadline = this.placeholder;
            this.jsEstimatedPublication = this.placeholder;
            this.jsContractStartDate = this.placeholder;
            this.jsContractEndDate = this.placeholder;
            this.buyerOrganisationName = this.placeholder;
            this.contactName = this.placeholder;
            this.contactEmail = this.placeholder;
            this.contactNumber = this.placeholder;
            this.contactAddress = new Address();
            this.contactAddress.line1 = this.placeholder;
            this.contactAddress.line2 = this.placeholder;
            this.contactAddress.city = this.placeholder;
            this.contactAddress.postcode = this.placeholder;
            this.url = this.placeholder;
            this.secureUrl = this.placeholder;
            this.description = this.placeholder;
            this.buyersDescription = this.placeholder;
        }
    }
}

export class TenderNote {
    createdBy: string;
    jsCreated: string;
    content: string;
    jsReminder: string;
}

export class NoticeDocument {
    id: number;
    name: string;
    fileName: string;
    jsLastViewed: string;
}

export class TenderReminder {
    text: string;
    jsDue: string;
}

export class SentToBidManagementSearchRequest extends PagingRequest {
    
}

export class AddSubscriberNoticeNoteDto {
    noticeId: number;
    content: string;
    reminder: string;
    reminderTime: string;
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Client/app/models/tenders/tender.models.ts